/**
 * 패널 모듈 트리 canonical 데이터 — Vuex 반응성 밖 (C4)
 */
import {
  appendStringToTree,
  removeTreeNodeById
} from './flattenModuleTreeRows.js';
import {
  appendModuleToStringLazyIndex,
  getPanelTreeModuleChildren,
  registerStringModuleChildren,
  removeModuleFromStringLazyIndex
} from './panelTreeModuleChildrenIndex.js';
import {
  compareModuleListItems,
  comparePanelTreeListItems,
  findPanelTreeInsertIndex,
  insertModuleTreeNodeInSeqOrder,
  sortPanelTreeListsInPlace
} from './panelModuleSeqOrder.js';
import { getDataByLevel } from '@/utils/siteDesignDataFlattener.js';
import { moduleDomainToTreeFields, moduleFromApi } from './panelHierarchyMappers.js';
import {
  panelPerfMark,
  panelPerfMeasure,
  panelPerfCountSummary
} from './panelPlanningPerfLog.js';

/** @type {object[]} */
let panelTreeRef = [];
const listeners = new Set();

function notifyPanelTreeChange() {
  listeners.forEach(listener => {
    try {
      listener();
    } catch (error) {
      console.error('[panelTree] listener failed', error);
    }
  });
}

/** in-place 트리 변경 후 UI 갱신 (낙관적 업데이트 등) */
export function notifyPanelTreeLocalChange() {
  notifyPanelTreeChange();
}

export function subscribePanelTreeLocalRegistry(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getPanelTreeLocalRegistry() {
  return panelTreeRef;
}

export function clearPanelTreeLocalRegistry() {
  panelTreeRef = [];
  notifyPanelTreeChange();
}

function summarizeTreeCounts(treeData) {
  const arrayCount = treeData?.length ?? 0;
  let stringCount = 0;
  let modulesHydrated = 0;
  let modulesLazy = 0;
  (treeData || []).forEach(arrayNode => {
    const strings = arrayNode?.children || [];
    stringCount += strings.length;
    strings.forEach(stringNode => {
      const hydrated = stringNode?.children?.length ?? 0;
      if (hydrated > 0) {
        modulesHydrated += hydrated;
      } else {
        modulesLazy += stringNode?.moduleChildCount ?? 0;
      }
    });
  });
  return { arrays: arrayCount, strings: stringCount, modulesHydrated, modulesLazy };
}

export function setPanelTreeLocalRegistry(treeData) {
  panelPerfMark('panelTree:setLocal:start');
  const next = treeData || [];
  sortPanelTreeListsInPlace(next);
  panelTreeRef = next;
  panelPerfCountSummary('panelTree:setLocal:counts', summarizeTreeCounts(next));
  panelPerfMeasure('panelTree:setLocal', 'panelTree:setLocal:start');
  notifyPanelTreeChange();
}

export function hydrateStringTreeModulesInRegistry(stringNodeId, moduleNodes) {
  if (!stringNodeId || !moduleNodes?.length) return;
  for (const arrayNode of panelTreeRef) {
    const childList = arrayNode.children;
    if (!childList?.length) continue;
    const idx = childList.findIndex(s => s.id === stringNodeId);
    if (idx < 0) continue;
    const stringNode = childList[idx];
    childList[idx] = {
      ...stringNode,
      children: moduleNodes,
      lazyModules: false
    };
    notifyPanelTreeChange();
    return;
  }
}

function sortSiblingListContaining(list, nodeId) {
  const target = String(nodeId);
  if (!list?.length) {
    return false;
  }
  for (let i = 0; i < list.length; i++) {
    if (String(list[i].id) === target) {
      list.sort(comparePanelTreeListItems);
      return true;
    }
    if (sortSiblingListContaining(list[i].children, nodeId)) {
      return true;
    }
  }
  return false;
}

export function addArrayToPanelTree(newArray) {
  if (!newArray) return;
  const idx = findPanelTreeInsertIndex(panelTreeRef, newArray);
  panelTreeRef.splice(idx, 0, newArray);
  notifyPanelTreeChange();
}

export function addStringToPanelTree(arrayNodeId, stringNode) {
  if (!arrayNodeId || !stringNode) return;
  panelTreeRef = appendStringToTree(panelTreeRef, arrayNodeId, stringNode);
  notifyPanelTreeChange();
}

export function removeArrayFromPanelTreeByIndex(index) {
  if (index < 0 || index >= panelTreeRef.length) return;
  panelTreeRef.splice(index, 1);
  notifyPanelTreeChange();
}

export function removePanelTreeNodeById(nodeId) {
  if (nodeId == null) return;
  panelTreeRef = removeTreeNodeById(panelTreeRef, nodeId);
  notifyPanelTreeChange();
}

/** Module 삭제 후 String 스켈레톤의 moduleChildCount 갱신 */
export function decrementStringModuleChildCount(stringNodeId) {
  if (!stringNodeId) {
    return;
  }
  for (const arrayNode of panelTreeRef) {
    const childList = arrayNode.children;
    if (!childList?.length) {
      continue;
    }
    const idx = childList.findIndex(s => s.id === stringNodeId);
    if (idx < 0) {
      continue;
    }
    const stringNode = childList[idx];
    const count = Math.max(0, (stringNode.moduleChildCount ?? 0) - 1);
    childList[idx] = { ...stringNode, moduleChildCount: count };
    notifyPanelTreeChange();
    return;
  }
}

/** Module 추가·이동 후 String 스켈레톤의 moduleChildCount 갱신 */
export function incrementStringModuleChildCount(stringNodeId) {
  if (!stringNodeId) {
    return;
  }
  for (const arrayNode of panelTreeRef) {
    const childList = arrayNode.children;
    if (!childList?.length) {
      continue;
    }
    const idx = childList.findIndex(s => s.id === stringNodeId);
    if (idx < 0) {
      continue;
    }
    const stringNode = childList[idx];
    const count = (stringNode.moduleChildCount ?? 0) + 1;
    childList[idx] = { ...stringNode, moduleChildCount: count };
    notifyPanelTreeChange();
    return;
  }
}

function isStringModulesHydrated(stringNode) {
  return Boolean(stringNode && stringNode.lazyModules === false);
}

function findStringNodeById(stringNodeId) {
  if (!stringNodeId) {
    return null;
  }
  const target = String(stringNodeId);
  for (const arrayNode of panelTreeRef) {
    const found = (arrayNode.children || []).find(
      node => node.type === 'string' && String(node.id) === target
    );
    if (found) {
      return found;
    }
  }
  return null;
}

/** String Module 목록 스냅샷 (rollback용) */
export function snapshotStringModulesState(stringNodeId) {
  const stringNode = findStringNodeById(stringNodeId);
  if (!stringNode) {
    return null;
  }
  return {
    stringNodeId,
    children: (stringNode.children || []).map(node => ({ ...node })),
    lazyIndex: getPanelTreeModuleChildren(stringNodeId).map(node => ({ ...node })),
    moduleChildCount: stringNode.moduleChildCount ?? 0,
    lazyModules: stringNode.lazyModules
  };
}

/** String Module 목록 스냅샷 복구 */
export function restoreStringModulesState(snapshot) {
  if (!snapshot?.stringNodeId) {
    return;
  }
  const stringNode = findStringNodeById(snapshot.stringNodeId);
  if (!stringNode) {
    return;
  }

  stringNode.children = snapshot.children.map(node => ({ ...node }));
  registerStringModuleChildren(
    snapshot.stringNodeId,
    snapshot.lazyIndex.map(node => ({ ...node }))
  );
  stringNode.moduleChildCount = snapshot.moduleChildCount;
  stringNode.lazyModules = snapshot.lazyModules;
  notifyPanelTreeChange();
}

function moduleNodeMatches(node, sourceModule) {
  if (!node || !sourceModule) {
    return false;
  }
  if (sourceModule.id != null && String(node.id) === String(sourceModule.id)) {
    return true;
  }
  const seq = sourceModule.solarModuleSeq ?? sourceModule.seq;
  if (seq == null || seq === '') {
    return false;
  }
  return String(node.solarModuleSeq ?? node.seq ?? '') === String(seq);
}

function removeModuleFromStringNode(stringNode, sourceModule) {
  if (!stringNode || !sourceModule) {
    return null;
  }

  let removed = null;

  const childIdx = (stringNode.children || []).findIndex(node => moduleNodeMatches(node, sourceModule));
  if (childIdx !== -1) {
    [removed] = stringNode.children.splice(childIdx, 1);
  }

  const lazyChildren = getPanelTreeModuleChildren(stringNode.id);
  const lazyIdx = lazyChildren.findIndex(node => moduleNodeMatches(node, sourceModule));
  if (lazyIdx !== -1) {
    if (!removed) {
      removed = lazyChildren[lazyIdx];
    }
    removeModuleFromStringLazyIndex(stringNode.id, lazyChildren[lazyIdx].id);
  } else if (removed?.id != null) {
    removeModuleFromStringLazyIndex(stringNode.id, removed.id);
  }

  if (removed) {
    decrementStringModuleChildCount(stringNode.id);
  }

  return removed;
}

function appendModuleToStringNode(stringNode, moduleNode) {
  if (!stringNode || !moduleNode) {
    return;
  }

  const alreadyInChildren = (stringNode.children || []).some(node =>
    moduleNodeMatches(node, moduleNode)
  );
  const lazyChildren = getPanelTreeModuleChildren(stringNode.id);
  const alreadyInLazy = lazyChildren.some(node => moduleNodeMatches(node, moduleNode));

  if (isStringModulesHydrated(stringNode)) {
    if (!stringNode.children) {
      stringNode.children = [];
    }
    if (!alreadyInChildren) {
      insertModuleTreeNodeInSeqOrder(stringNode.children, moduleNode);
    }
    if (!alreadyInLazy) {
      appendModuleToStringLazyIndex(stringNode.id, moduleNode);
    }
    if (!alreadyInChildren || !alreadyInLazy) {
      incrementStringModuleChildCount(stringNode.id);
    }
    return;
  }

  if (!alreadyInLazy) {
    appendModuleToStringLazyIndex(stringNode.id, moduleNode);
    incrementStringModuleChildCount(stringNode.id);
  }
  if (stringNode.children?.length) {
    stringNode.children = [];
  }
}

/** freeze된 노드는 슬롯 교체, 아니면 필드만 수정 (트리 root 참조 유지) */
function patchTreeNodeById(list, predicate, applyPatch) {
  if (!list?.length) {
    return false;
  }
  for (let i = 0; i < list.length; i++) {
    const node = list[i];
    if (predicate(node)) {
      list[i] = applyPatch({ ...node });
      return true;
    }
    if (patchTreeNodeById(node.children, predicate, applyPatch)) {
      return true;
    }
  }
  return false;
}

export function renamePanelTreeNode(nodeId, newName) {
  if (nodeId == null || newName == null) {
    return false;
  }
  const target = String(nodeId);
  const updated = patchTreeNodeById(
    panelTreeRef,
    node => String(node.id) === target,
    node => ({ ...node, name: newName })
  );
  if (updated) {
    sortSiblingListContaining(panelTreeRef, target);
    notifyPanelTreeChange();
  }
  return updated;
}

/**
 * 트리 노드 이름·표시 번호 in-place 갱신 (펼침·스크롤 유지)
 * commitDerivedTreeData / setPanelTreeLocalRegistry 전체 교체 대신 사용
 */
export function updatePanelTreeNodeEntityDisplayInPlace(
  nodeId,
  { entityType, newName, displayId } = {}
) {
  if (nodeId == null || !entityType || newName == null) {
    return false;
  }
  const target = String(nodeId);
  const displayIdStr = displayId != null && displayId !== '' ? String(displayId) : null;
  const updated = patchTreeNodeById(
    panelTreeRef,
    node => String(node.id) === target && node.type === entityType,
    node => {
      const patched = { ...node, name: newName };
      if (displayIdStr) {
        if (entityType === 'array') {
          patched.arrayId = displayIdStr;
        } else if (entityType === 'string') {
          patched.stringId = displayIdStr;
          patched.solarStringId = displayIdStr;
          patched.displayNo = displayIdStr;
        } else if (entityType === 'module') {
          patched.moduleId = displayIdStr;
          patched.solarModuleId = displayIdStr;
          patched.displayNo = displayIdStr;
        }
      }
      return patched;
    }
  );

  if (updated) {
    sortSiblingListContaining(panelTreeRef, target);
    notifyPanelTreeChange();
  }
  return updated;
}

export function updateArrayGroupInPanelTree(arrayId, updatedData) {
  const arrayIndex = panelTreeRef.findIndex(array => array.id === arrayId);
  if (arrayIndex === -1) return;
  panelTreeRef[arrayIndex] = {
    ...panelTreeRef[arrayIndex],
    ...updatedData
  };
  notifyPanelTreeChange();
}

export function removeArrayGroupFromPanelTree(arrayId) {
  const arrayIndex = panelTreeRef.findIndex(array => array.id === arrayId);
  if (arrayIndex === -1) return;
  panelTreeRef.splice(arrayIndex, 1);
  notifyPanelTreeChange();
}

/** String 이동 — in-place (펼침 상태 보존) */
export function moveStringInPanelTreeInPlace(sourceString, targetArray) {
  let moved = false;
  for (const arr of panelTreeRef) {
    const idx = arr.children?.findIndex(c => c.id === sourceString.id) ?? -1;
    if (idx === -1) continue;
    const [removed] = arr.children.splice(idx, 1);
    const targetArr =
      arr.id === targetArray.id ? arr : panelTreeRef.find(a => a.id === targetArray.id);
    if (targetArr) {
      const insertIdx = findPanelTreeInsertIndex(targetArr.children || [], removed);
      if (!targetArr.children) {
        targetArr.children = [];
      }
      targetArr.children.splice(insertIdx, 0, removed);
    }
    moved = true;
    break;
  }
  if (moved) notifyPanelTreeChange();
  return moved;
}

/** Module 이동 — in-place (lazy String은 인덱스에 병합, hydrate된 String은 children에 추가) */
export function moveModuleInPanelTreeInPlace(sourceModule, targetString) {
  if (!sourceModule?.id || !targetString?.id) {
    return false;
  }

  let removed = null;

  for (const arrayNode of panelTreeRef) {
    for (const stringNode of arrayNode.children || []) {
      if (stringNode.type !== 'string') {
        continue;
      }
      const candidate = removeModuleFromStringNode(stringNode, sourceModule);
      if (candidate) {
        removed = candidate;
        break;
      }
    }
    if (removed) {
      break;
    }
  }

  if (!removed) {
    return false;
  }

  const targetStringNode = findStringNodeById(targetString.id);
  if (!targetStringNode) {
    notifyPanelTreeChange();
    return false;
  }

  appendModuleToStringNode(targetStringNode, removed);
  notifyPanelTreeChange();
  return true;
}

function buildModuleTreeNodesFromFlatRecords(stringModules) {
  return (stringModules || [])
    .map((module, moduleIndex) => {
      const moduleDomain = moduleFromApi(module);
      return {
        ...moduleDomainToTreeFields(moduleDomain),
        name: module.name || `Module ${moduleDomain.displayNo || moduleIndex + 1}`,
        children: []
      };
    })
    .sort(compareModuleListItems);
}

/** flattened 기준으로 String의 lazy 인덱스·hydrate children 동기화 */
export function syncStringModuleTreeFromFlattened(flattenedSiteDesign, stringNodeId, stringSeq) {
  if (!flattenedSiteDesign || !stringNodeId || stringSeq == null || stringSeq === '') {
    return;
  }

  const seqKey = String(stringSeq);
  const modules = getDataByLevel(flattenedSiteDesign, 'module').filter(
    module => String(module.parentId) === seqKey
  );
  const moduleNodes = buildModuleTreeNodesFromFlatRecords(modules);
  registerStringModuleChildren(stringNodeId, moduleNodes);

  for (const arrayNode of panelTreeRef) {
    const childList = arrayNode.children;
    if (!childList?.length) {
      continue;
    }
    const idx = childList.findIndex(node => node.id === stringNodeId);
    if (idx < 0) {
      continue;
    }

    const stringNode = childList[idx];
    const keepHydrated = stringNode.lazyModules === false;
    childList[idx] = {
      ...stringNode,
      moduleChildCount: moduleNodes.length,
      lazyModules: moduleNodes.length > 0 ? !keepHydrated : false,
      children: keepHydrated ? moduleNodes : []
    };
    notifyPanelTreeChange();
    return;
  }
}

export function findPanelTreeArrayIndexById(arrayId) {
  return panelTreeRef.findIndex(item => item.id === arrayId);
}

export function collectPanelTreeArrayDisplayNumbers() {
  return panelTreeRef
    .filter(item => item.type === 'array')
    .map(item => {
      const fromName = String(item.name ?? '').match(/(\d+)\s*$/);
      if (fromName) {
        return parseInt(fromName[1], 10);
      }
      const fromId = parseInt(String(item.arrayId ?? ''), 10);
      return Number.isFinite(fromId) && fromId > 0 ? fromId : 0;
    })
    .filter(num => num > 0);
}
